import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Layers,
  PlusCircle,
  PlayCircle,
  PauseCircle,
  Loader2,
  Calendar as CalendarIcon,
  CheckCircle2,
  XCircle,
  Activity,
  AlertTriangle,
} from "lucide-react";

const STATUS_COLOR = {
  active: "bg-emerald-600",
  paused: "bg-amber-500",
  cancelled: "bg-stone-500",
  completed: "bg-sky-600",
};

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function topicCounts(s) {
  const counts = { ready: 0, generating: 0, queued: 0, published: 0,
                   failed: 0 };
  for (const t of s.topics || []) {
    if (counts[t.status] !== undefined) counts[t.status] += 1;
  }
  return counts;
}

export default function Series() {
  const [items, setItems] = useState(null);
  const [worker, setWorker] = useState(null);

  const refresh = async () => {
    try {
      const [r, w] = await Promise.all([
        api.get("/series"),
        api.get("/series/_worker/status").catch(() => ({ data: null })),
      ]);
      setItems(r.data);
      setWorker(w.data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            data-testid="series-title"
            className="text-3xl font-bold text-stone-800 sm:text-4xl"
          >
            Series
          </h1>
          <p className="mt-1 text-stone-600">
            Create a multi-episode batch and let Dom Studio publish on a
            schedule.
          </p>
        </div>
        <Button
          asChild
          className="bg-orange-500 text-white hover:bg-orange-600"
          data-testid="series-new-button"
        >
          <Link to="/series/new">
            <PlusCircle className="mr-2 h-4 w-4" /> New series
          </Link>
        </Button>
      </div>

      {worker ? (
        <div
          data-testid="series-worker-banner"
          className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-xs ${
            worker.stalled || !worker.worker_running
              ? "border-rose-300 bg-rose-50 text-rose-800"
              : worker.fal_circuit?.open
              ? "border-amber-300 bg-amber-50 text-amber-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          {worker.stalled || !worker.worker_running ? (
            <AlertTriangle className="h-3.5 w-3.5" />
          ) : (
            <Activity className="h-3.5 w-3.5" />
          )}
          <span className="font-semibold">
            Worker:{" "}
            {worker.worker_running
              ? worker.stalled
                ? "stalled"
                : "running"
              : "down"}
          </span>
          <span>· queue: {worker.queue_depth}</span>
          <span>· generating: {worker.currently_generating}</span>
          <span>
            · done: {worker.heartbeat?.total_completed || 0} / failed:{" "}
            {worker.heartbeat?.total_failed || 0}
          </span>
          {worker.heartbeat?.active_topic_title ? (
            <span className="font-medium">
              · now: {worker.heartbeat.active_topic_title}
            </span>
          ) : null}
          {worker.fal_circuit?.open ? (
            <span className="ml-auto rounded bg-amber-200 px-2 py-0.5 font-medium">
              ⚠ fal.ai degraded — clips falling back to Ken Burns
            </span>
          ) : null}
        </div>
      ) : null}

      {items === null ? (
        <div className="flex items-center gap-2 text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" /> loading…
        </div>
      ) : items.length === 0 ? (
        <Card
          data-testid="series-empty"
          className="border-dashed border-orange-300 bg-white/60"
        >
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-200 to-rose-200 text-orange-600">
              <Layers className="h-7 w-7" />
            </div>
            <div className="text-lg font-semibold text-stone-800">
              No series yet
            </div>
            <p className="max-w-md text-sm text-stone-600">
              Pick 3-10 nursery rhymes, learning topics, or stories — Dom
              Studio will generate every video and publish them to YouTube
              on a schedule you set.
            </p>
            <Button
              asChild
              className="bg-orange-500 text-white hover:bg-orange-600"
            >
              <Link to="/series/new">
                <PlusCircle className="mr-2 h-4 w-4" /> Create your first series
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((s) => {
            const c = topicCounts(s);
            const total = s.topics.length;
            const done = c.ready + c.published;
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <Link
                key={s.id}
                to={`/series/${s.id}`}
                data-testid={`series-card-${s.id}`}
              >
                <Card className="border-orange-100 bg-white/80 transition hover:border-orange-300 hover:shadow-md">
                  <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                    <div>
                      <CardTitle className="text-lg">{s.name}</CardTitle>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-stone-500">
                        {s.content_type} · {s.length} · {total} topics
                      </p>
                    </div>
                    <Badge className={STATUS_COLOR[s.status] || "bg-stone-500"}>
                      {s.status}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-1 flex justify-between text-xs text-stone-600">
                      <span>
                        {done}/{total} ready{c.published ? ` · ${c.published} live` : ""}
                      </span>
                      <span className="font-semibold">{pct}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-500 to-rose-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                      {c.queued ? (
                        <span className="rounded bg-stone-100 px-1.5 py-0.5 text-stone-700">
                          {c.queued} queued
                        </span>
                      ) : null}
                      {c.generating ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                          {c.generating} generating
                        </span>
                      ) : null}
                      {c.ready ? (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">
                          <CheckCircle2 className="mr-1 inline h-3 w-3" />
                          {c.ready} ready
                        </span>
                      ) : null}
                      {c.published ? (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800">
                          <PlayCircle className="mr-1 inline h-3 w-3" />
                          {c.published} live
                        </span>
                      ) : null}
                      {c.failed ? (
                        <span className="rounded bg-rose-100 px-1.5 py-0.5 text-rose-800">
                          <XCircle className="mr-1 inline h-3 w-3" />
                          {c.failed} failed
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-stone-500">
                      <span>
                        <CalendarIcon className="mr-1 inline h-3 w-3" />
                        {s.schedule?.cadence || "no schedule"} ·{" "}
                        {s.schedule?.time_local || ""}
                      </span>
                      <span>
                        Next: {fmtRelative(s.next_publish_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
